import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Menu, MenuItem, Paper, Stack, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import reportTemplateServices from "app/services/automations/report-template-services";
import ReportTemplateRow from "./ReportTemplateRow";

const LIST_PATH = "/automations/reports";

// Plantillas de reportes: las propias y las compartidas de la empresa, cada una lista para generarse eligiendo solo las
// fechas.
const ReportTemplatesList = ({ stores, onCreate, onQuickReport }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [menu, setMenu] = useState(null);
    const notice = location.state?.notice;

    const load = useCallback(() => {
        setLoading(true);
        reportTemplateServices.list()
            .then((resp) => setTemplates(resp?.data || []))
            .catch(() => setError("No se pudieron cargar las plantillas."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const closeMenu = () => setMenu(null);

    const edit = (template) => navigate(`${LIST_PATH}/templates/${template.id}`, { state: { from: LIST_PATH } });

    // Una copia personal, que se revisa en el editor antes de guardarla.
    const duplicate = (template) => navigate(`${LIST_PATH}/templates/new`, {
        state: {
            from: LIST_PATH,
            draft: {
                name: `Copia de ${template.name}`, report_type: template.report_type, store_id: template.store?.id || "",
                default_period: template.default_period, shared: false, params: template.params,
            },
        },
    });

    const remove = async (template) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Eliminar la plantilla «${template.name}»?${template.shared ? " Está compartida: dejará de estar disponible para toda la empresa." : ""}`)) return;
        await reportTemplateServices.remove(template.id);
        load();
    };

    if (loading) return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;

    const mine = templates.filter((template) => template.mine);
    const others = templates.filter((template) => !template.mine);
    const section = (title, items) => !!items.length && (
        <Stack spacing={1.25}>
            {!!mine.length && !!others.length && <Typography variant="h5" color="text.secondary">{title}</Typography>}
            {items.map((template) => (
                <ReportTemplateRow key={template.id} template={template} stores={stores}
                                   onOpenMenu={(anchorEl, item) => setMenu({ anchorEl, item })} />
            ))}
        </Stack>
    );

    return (
        <Stack spacing={2.5}>
            {!!notice && <Alert severity="success">{notice}</Alert>}
            {!templates.length && (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2.5, borderStyle: "dashed", textAlign: "center" }}>
                    <Typography variant="body1" fontWeight={600}>Todavía no hay plantillas</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Guarda la configuración de un reporte para generarlo después eligiendo solo las fechas.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
                        <Button variant="contained" onClick={onCreate}>Crear una plantilla</Button>
                        <Button onClick={onQuickReport}>Armar un reporte rápido</Button>
                    </Stack>
                </Paper>
            )}
            {section("Tus plantillas", mine)}
            {section("Compartidas en la empresa", others)}

            <Menu anchorEl={menu?.anchorEl} open={!!menu} onClose={closeMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem disabled={!menu?.item?.can_edit} onClick={() => { const { item } = menu; closeMenu(); edit(item); }}>
                    <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Editar
                </MenuItem>
                <MenuItem onClick={() => { const { item } = menu; closeMenu(); duplicate(item); }}>
                    <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Duplicar
                </MenuItem>
                <MenuItem sx={{ color: "error.main" }} disabled={!menu?.item?.can_edit}
                          onClick={() => { const { item } = menu; closeMenu(); remove(item); }}>
                    <DeleteOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Eliminar
                </MenuItem>
            </Menu>
        </Stack>
    );
};

export default ReportTemplatesList;
